/** @typedef {'full' | 'abbrev' | 'dot'} ProjectPillDisplayTier */

/**
 * Non-circular pills-column budget: row content width minus title minimum and fixed trailing chrome.
 * Does not use the pills column's own laid-out width (that tracks rendered tier).
 * @param {{
 *   contentWidthPx: number,
 *   titleMinWidthPx: number,
 *   titleScrollWidthPx?: number,
 *   contentColumnGapPx: number,
 *   durationColWidthPx: number,
 *   metadataSiblingWidthsPx?: number[],
 *   metadataFlexGapPx: number,
 * }} params
 */
/** Gap between checkbox, title, and pill on scheduled calendar title rows. */
export const SCHEDULED_TASK_TITLE_ROW_GAP_PX = 4;

/**
 * Non-circular pill budget for a scheduled task title row (checkbox + title + pill).
 * @param {{
 *   rowWidthPx: number,
 *   checkboxWidthPx: number,
 *   titleMinWidthPx: number,
 *   rowGapPx?: number,
 * }} params
 */
export function computeScheduledTaskTitleRowPillBudgetPx({
  rowWidthPx,
  checkboxWidthPx,
  titleMinWidthPx,
  rowGapPx = SCHEDULED_TASK_TITLE_ROW_GAP_PX,
}) {
  const gapCount = 2;
  return Math.max(
    0,
    rowWidthPx -
      checkboxWidthPx -
      titleMinWidthPx -
      gapCount * rowGapPx,
  );
}

export function computeTaskRowPillsColBudgetPx({
  contentWidthPx,
  titleMinWidthPx,
  titleScrollWidthPx = 0,
  contentColumnGapPx,
  durationColWidthPx,
  metadataSiblingWidthsPx = [],
  metadataFlexGapPx,
}) {
  const titleReservePx = Math.max(
    titleMinWidthPx,
    titleScrollWidthPx,
  );
  const metadataChildCount = metadataSiblingWidthsPx.length + 2;
  const metadataGapTotal =
    metadataChildCount > 1
      ? (metadataChildCount - 1) * metadataFlexGapPx
      : 0;
  const otherSiblingsTotal = metadataSiblingWidthsPx.reduce(
    (sum, w) => sum + w,
    0,
  );
  return Math.max(
    0,
    contentWidthPx -
      titleReservePx -
      contentColumnGapPx -
      durationColWidthPx -
      otherSiblingsTotal -
      metadataGapTotal,
  );
}

/**
 * Pick project-pill display tier from measured widths (list rows only).
 * @param {{
 *   pillsColWidthPx: number,
 *   contextPillsWidthPx: number,
 *   gapPx: number,
 *   projectWidthsPx: { full: number, abbrev: number, dot: number },
 * }} params
 * @returns {ProjectPillDisplayTier}
 */
export function resolveProjectPillTier({
  pillsColWidthPx,
  contextPillsWidthPx,
  gapPx,
  projectWidthsPx,
}) {
  const available =
    pillsColWidthPx - contextPillsWidthPx - (contextPillsWidthPx > 0 ? gapPx : 0);

  if (available >= projectWidthsPx.full) return 'full';
  if (available >= projectWidthsPx.abbrev) return 'abbrev';
  return 'dot';
}
